import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe } from "lucide-react";
import { Logo } from "./Logo";
import { Button } from "./primitives";
import { useLang } from "../i18n";

export const Navbar = () => {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  const links = [
    { to: "/", label: t.nav.home }, { to: "/for-brands", label: t.nav.brands },
    { to: "/for-publishers", label: t.nav.publishers }, { to: "/solutions", label: t.nav.solutions },
    { to: "/how-it-works", label: t.nav.how }, { to: "/about", label: t.nav.about },
    { to: "/insights", label: t.nav.insights }, { to: "/contact", label: t.nav.contact },
  ];

  return (
    <motion.header
      initial={{ y: -80 }} animate={{ y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "backdrop-blur-xl bg-white/85 shadow-[0_8px_32px_rgba(53,53,53,0.06)]" : "bg-transparent"}`}
      data-testid="navbar"
    >
      <div className="mx-auto max-w-[1500px] px-5 md:px-10 flex items-center justify-between h-20">
        <Logo variant="primary" height={40} />

        <nav className="hidden xl:flex items-center gap-1">
          {links.map((l) => {
            const active = loc.pathname === l.to;
            return (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label}`}
                className={`relative px-4 py-2 text-sm font-bold transition-colors ${active ? "text-wanm-red" : "text-wanm-dark/80 hover:text-wanm-dark"}`}>
                {l.label}
                {active && <motion.span layoutId="navdot" className="absolute -bottom-0.5 left-4 right-4 h-0.5 wanm-gradient-bg rounded-full" />}
              </Link>
            );
          })}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          <button onClick={toggle} data-testid="lang-toggle"
            className="flex items-center gap-1.5 text-sm font-bold text-wanm-dark/80 hover:text-wanm-red transition-colors px-2">
            <Globe size={16} /> {lang.toUpperCase()}
          </button>
          <Button variant="outline" to="/for-publishers" testId="nav-cta-publisher" className="!px-5 !py-2.5 !text-sm">{t.cta.publisher}</Button>
          <Button variant="primary" to="/contact" testId="nav-cta-work" className="!px-5 !py-2.5 !text-sm">{t.cta.work}</Button>
        </div>

        <div className="flex xl:hidden items-center gap-3">
          <button onClick={toggle} data-testid="lang-toggle-mobile" className="flex items-center gap-1 text-sm font-bold text-wanm-dark">
            <Globe size={16} /> {lang.toUpperCase()}
          </button>
          <button onClick={() => setOpen(!open)} data-testid="menu-toggle" className="text-wanm-dark p-1">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="xl:hidden bg-white border-t border-wanm-offwhite overflow-hidden" data-testid="mobile-menu">
            <div className="px-5 py-6 flex flex-col gap-1">
              {links.map((l) => (
                <Link key={l.to} to={l.to} className="py-3 text-lg font-bold text-wanm-dark border-b border-wanm-offwhite/60">{l.label}</Link>
              ))}
              <div className="flex flex-col gap-3 mt-5">
                <Button variant="outline" to="/for-publishers">{t.cta.publisher}</Button>
                <Button variant="primary" to="/contact">{t.cta.work}</Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
